import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Loader2, Mail, MapPin, Pencil, Phone, Plus, RefreshCw, Search, Trash2, User, Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  PanelTableCard, PanelTableHeader, PanelTableFrame, PanelTableHeadRow, PanelTableHead,
  PanelTableBody, PanelTableRow, PanelTableCell, PanelTableEmpty, PanelTableActions,
} from "@/components/ui/panel-table";
import {
  EntityAvatar, EntityStatPill, EntityInfoBlock, EntityDetailRow, EntityModalHero,
} from "@/components/companies/entity-ui";

const emptyForm = (companyId = "") => ({
  company_id: companyId,
  ad: "",
  telefon: "",
  email: "",
  adres: "",
  yetkili_ad_soyad: "",
  yetkili_telefon: "",
  yetkili_email: "",
  yetkili_unvan: "",
  notlar: "",
});

const HospitalsPage = ({
  apiBase, isDark, textClass, labelClass, cardClass, inputClass,
  companies = [], isSuperAdmin = false, userCompanyId = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [companyFilter, setCompanyFilter] = useState(isSuperAdmin ? "all" : String(userCompanyId || ""));
  const [searchTerm, setSearchTerm] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => emptyForm(isSuperAdmin ? "" : String(userCompanyId || "")));

  const defaultCompanyId = useMemo(() => {
    if (!isSuperAdmin && userCompanyId) return String(userCompanyId);
    if (companyFilter && companyFilter !== "all") return companyFilter;
    return companies[0]?.id ? String(companies[0].id) : "";
  }, [isSuperAdmin, userCompanyId, companyFilter, companies]);

  const stats = useMemo(() => ({
    total: hospitals.length,
    doctors: hospitals.reduce((sum, h) => sum + (h.doctor_count || 0), 0),
  }), [hospitals]);

  const fetchHospitals = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isSuperAdmin && companyFilter && companyFilter !== "all") params.company_id = companyFilter;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const res = await axios.get(`${apiBase}/hospitals`, { params });
      setHospitals(res.data?.hospitals || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hastaneler yüklenemedi");
      setHospitals([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, companyFilter, isSuperAdmin, searchTerm]);

  useEffect(() => { fetchHospitals(); }, [fetchHospitals]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm(defaultCompanyId));
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      company_id: String(row.company_id || defaultCompanyId),
      ad: row.ad || "",
      telefon: row.telefon || "",
      email: row.email || "",
      adres: row.adres || "",
      yetkili_ad_soyad: row.yetkili_ad_soyad || "",
      yetkili_telefon: row.yetkili_telefon || "",
      yetkili_email: row.yetkili_email || "",
      yetkili_unvan: row.yetkili_unvan || "",
      notlar: row.notlar || "",
    });
    setDetailOpen(false);
    setFormOpen(true);
  };

  const openDetail = (row) => {
    setSelected(row);
    setDetailOpen(true);
  };

  const handleSave = async () => {
    if (!form.ad.trim()) { toast.error("Hastane adı zorunludur"); return; }
    if (!form.company_id) { toast.error("Firma seçimi zorunludur"); return; }
    setSaving(true);
    try {
      const payload = { ...form, company_id: Number(form.company_id) };
      if (editing?.id) {
        await axios.put(`${apiBase}/hospitals?id=${editing.id}`, payload);
        toast.success("Hastane güncellendi");
      } else {
        await axios.post(`${apiBase}/hospitals`, payload);
        toast.success("Hastane eklendi");
      }
      setFormOpen(false);
      await fetchHospitals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.ad}" hastanesi silinsin mi?`)) return;
    try {
      await axios.delete(`${apiBase}/hospitals?id=${row.id}`);
      toast.success("Hastane silindi");
      setDetailOpen(false);
      await fetchHospitals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  const rowHover = isDark
    ? "hover:bg-indigo-500/5 cursor-pointer group"
    : "hover:bg-indigo-50/60 cursor-pointer group";

  return (
    <>
      <PanelTableCard isDark={isDark} className={cardClass}>
        <PanelTableHeader
          isDark={isDark}
          textClass={textClass}
          labelClass={labelClass}
          accent="purple"
          icon={Building2}
          title="Hastane Yönetimi"
          description="Hastane kayıtları, iletişim ve yetkili kişi bilgileri"
          toolbar={(
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={fetchHospitals} disabled={loading} className={isDark ? "border-slate-600" : ""}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
              <Button type="button" size="sm" onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-600/20">
                <Plus className="w-4 h-4 mr-1" /> Yeni Hastane
              </Button>
            </div>
          )}
        />

        <div className="mx-4 mt-3 flex flex-wrap gap-2">
          <EntityStatPill label="Hastane" value={stats.total} tone="indigo" isDark={isDark} />
          <EntityStatPill label="Bağlı Doktor" value={stats.doctors} tone="violet" isDark={isDark} />
        </div>

        <div className={`mx-4 mb-3 mt-3 p-3 rounded-xl border grid grid-cols-1 md:grid-cols-12 gap-3 ${isDark ? "border-slate-700/80 bg-slate-900/40" : "border-indigo-100 bg-indigo-50/30"}`}>
          {isSuperAdmin && (
            <div className="md:col-span-4">
              <label className={`text-xs font-medium ${labelClass} block mb-1.5`}>Firma</label>
              <Select value={companyFilter || "all"} onValueChange={setCompanyFilter}>
                <SelectTrigger className={`h-9 text-xs w-full ${inputClass}`}><SelectValue placeholder="Tüm firmalar" /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="all">Tüm firmalar</SelectItem>
                  {companies.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className={isSuperAdmin ? "md:col-span-8" : "md:col-span-12"}>
            <label className={`text-xs font-medium ${labelClass} block mb-1.5`}>Ara</label>
            <div className="relative">
              <Search className={`w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? "text-indigo-400" : "text-indigo-500"}`} />
              <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Hastane adı, telefon, yetkili..." className={`h-9 pl-9 ${inputClass}`} />
            </div>
          </div>
        </div>

        <PanelTableFrame isDark={isDark}>
          <PanelTableHeadRow isDark={isDark}>
            <PanelTableHead>Hastane</PanelTableHead>
            {isSuperAdmin && <PanelTableHead>Firma</PanelTableHead>}
            <PanelTableHead>İletişim</PanelTableHead>
            <PanelTableHead>Yetkili</PanelTableHead>
            <PanelTableHead className="text-right">Doktor</PanelTableHead>
            <PanelTableHead className="text-right w-24">İşlem</PanelTableHead>
          </PanelTableHeadRow>
          <PanelTableBody>
            {loading ? (
              <PanelTableEmpty colSpan={isSuperAdmin ? 6 : 5} labelClass={labelClass}>
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-500" />
              </PanelTableEmpty>
            ) : hospitals.length === 0 ? (
              <PanelTableEmpty colSpan={isSuperAdmin ? 6 : 5} labelClass={labelClass}>
                Hastane kaydı yok — yeni ekleyin
              </PanelTableEmpty>
            ) : hospitals.map((row) => (
              <PanelTableRow key={row.id} isDark={isDark} className={rowHover} onClick={() => openDetail(row)}>
                <PanelTableCell>
                  <div className="flex items-center gap-3">
                    <EntityAvatar name={row.ad} theme="indigo" icon={Building2} />
                    <div className="min-w-0">
                      <div className={`text-sm font-semibold ${textClass} group-hover:text-indigo-500 transition-colors`}>{row.ad}</div>
                      {row.adres && <div className={`text-[11px] mt-0.5 truncate max-w-[220px] ${labelClass}`}><MapPin className="w-3 h-3 inline mr-0.5 opacity-60" />{row.adres}</div>}
                    </div>
                  </div>
                </PanelTableCell>
                {isSuperAdmin && (
                  <PanelTableCell>
                    <Badge className={isDark ? "bg-violet-500/15 text-violet-300 border-violet-500/25" : "bg-violet-50 text-violet-700 border-violet-200"}>
                      {row.company_name || row.company_id}
                    </Badge>
                  </PanelTableCell>
                )}
                <PanelTableCell className="text-xs">
                  <div className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-emerald-500" />{row.telefon || "—"}</div>
                  {row.email && <div className={`flex items-center gap-1.5 mt-1 ${labelClass}`}><Mail className="w-3 h-3" />{row.email}</div>}
                </PanelTableCell>
                <PanelTableCell className="text-xs">
                  <div className="font-medium">{row.yetkili_ad_soyad || "—"}</div>
                  {row.yetkili_unvan && <div className={labelClass}>{row.yetkili_unvan}</div>}
                </PanelTableCell>
                <PanelTableCell className="text-right">
                  <Badge className={isDark ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>
                    <Users className="w-3 h-3 mr-1 inline" />{row.doctor_count ?? 0}
                  </Badge>
                </PanelTableCell>
                <PanelTableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <PanelTableActions>
                    <Button type="button" variant="ghost" size="icon" onClick={() => openEdit(row)} title="Düzenle"><Pencil className="w-4 h-4" /></Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => handleDelete(row)} className={isDark ? "text-rose-400" : "text-rose-600"} title="Sil"><Trash2 className="w-4 h-4" /></Button>
                  </PanelTableActions>
                </PanelTableCell>
              </PanelTableRow>
            ))}
          </PanelTableBody>
        </PanelTableFrame>
      </PanelTableCard>

      {/* Detay modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className={`max-w-lg p-0 overflow-hidden border-0 [&>button]:text-white [&>button]:opacity-90 [&>button]:hover:opacity-100 [&>button]:hover:bg-white/10 ${isDark ? "bg-slate-900 text-white" : "bg-white"}`}>
          {selected && (
            <>
              <EntityModalHero
                name={selected.ad}
                subtitle={selected.company_name || (isSuperAdmin ? `Firma #${selected.company_id}` : null)}
                theme="indigo"
                icon={Building2}
                badges={[
                  selected.yetkili_unvan,
                  `${selected.doctor_count ?? 0} doktor`,
                ].filter(Boolean)}
              />
              <div className="px-6 pt-4 pb-2 space-y-3">
                <EntityInfoBlock title="İletişim Bilgileri" icon={Phone} tone="emerald" isDark={isDark}>
                  <EntityDetailRow label="Telefon" value={selected.telefon} isDark={isDark} labelClass={labelClass} textClass={textClass} mono />
                  <EntityDetailRow label="E-posta" value={selected.email} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                  <EntityDetailRow label="Adres" value={selected.adres} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                </EntityInfoBlock>
                <EntityInfoBlock title="Yetkili Kişi" icon={User} tone="amber" isDark={isDark}>
                  <EntityDetailRow label="Ad Soyad" value={selected.yetkili_ad_soyad} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                  <EntityDetailRow label="Unvan" value={selected.yetkili_unvan} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                  <EntityDetailRow label="Telefon" value={selected.yetkili_telefon} isDark={isDark} labelClass={labelClass} textClass={textClass} mono />
                  <EntityDetailRow label="E-posta" value={selected.yetkili_email} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                </EntityInfoBlock>
                {selected.notlar && (
                  <EntityInfoBlock title="Notlar" tone="indigo" isDark={isDark}>
                    <p className={`text-sm ${textClass}`}>{selected.notlar}</p>
                  </EntityInfoBlock>
                )}
              </div>
              <DialogFooter className={`px-6 py-4 border-t ${isDark ? "border-slate-800 bg-slate-950/50" : "border-gray-100 bg-gray-50/50"}`}>
                <Button type="button" variant="outline" onClick={() => setDetailOpen(false)} className={isDark ? "border-slate-600" : ""}>Kapat</Button>
                <Button type="button" variant="outline" onClick={() => openEdit(selected)} className={isDark ? "border-indigo-500/40 text-indigo-300" : "border-indigo-200 text-indigo-700"}>
                  <Pencil className="w-4 h-4 mr-1" /> Düzenle
                </Button>
                <Button type="button" onClick={() => handleDelete(selected)} className="bg-rose-600 hover:bg-rose-500 text-white">
                  <Trash2 className="w-4 h-4 mr-1" /> Sil
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Form modal */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-indigo-500/15"><Building2 className="w-4 h-4 text-indigo-500" /></span>
              {editing ? "Hastane Düzenle" : "Yeni Hastane"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            {isSuperAdmin && (
              <div className="sm:col-span-2">
                <label className={`text-xs font-medium ${labelClass}`}>Firma *</label>
                <Select value={form.company_id || undefined} onValueChange={(v) => setForm((f) => ({ ...f, company_id: v }))}>
                  <SelectTrigger className={`mt-1 h-9 ${inputClass}`}><SelectValue placeholder="Firma seçin" /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    {companies.filter((c) => c.status === 1).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="sm:col-span-2">
              <label className={`text-xs font-medium ${labelClass}`}>Hastane Adı *</label>
              <Input value={form.ad} onChange={(e) => setForm((f) => ({ ...f, ad: e.target.value }))} className={`mt-1 h-9 ${inputClass}`} />
            </div>
            <div>
              <label className={`text-xs font-medium ${labelClass}`}>Telefon</label>
              <Input value={form.telefon} onChange={(e) => setForm((f) => ({ ...f, telefon: e.target.value }))} className={`mt-1 h-9 ${inputClass}`} />
            </div>
            <div>
              <label className={`text-xs font-medium ${labelClass}`}>E-posta</label>
              <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={`mt-1 h-9 ${inputClass}`} />
            </div>
            <div className="sm:col-span-2">
              <label className={`text-xs font-medium ${labelClass}`}>Adres</label>
              <Textarea value={form.adres} onChange={(e) => setForm((f) => ({ ...f, adres: e.target.value }))} rows={2} className={`mt-1 ${inputClass}`} />
            </div>
            <div className={`sm:col-span-2 rounded-xl border p-3 ${isDark ? "border-amber-500/20 bg-amber-500/5" : "border-amber-100 bg-amber-50/40"}`}>
              <p className={`text-xs font-semibold mb-2 ${isDark ? "text-amber-300" : "text-amber-800"}`}>Yetkili Kişi</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={`text-xs font-medium ${labelClass}`}>Ad Soyad</label>
                  <Input value={form.yetkili_ad_soyad} onChange={(e) => setForm((f) => ({ ...f, yetkili_ad_soyad: e.target.value }))} className={`mt-1 h-9 ${inputClass}`} />
                </div>
                <div>
                  <label className={`text-xs font-medium ${labelClass}`}>Unvan</label>
                  <Input value={form.yetkili_unvan} onChange={(e) => setForm((f) => ({ ...f, yetkili_unvan: e.target.value }))} className={`mt-1 h-9 ${inputClass}`} />
                </div>
                <div>
                  <label className={`text-xs font-medium ${labelClass}`}>Yetkili Telefon</label>
                  <Input value={form.yetkili_telefon} onChange={(e) => setForm((f) => ({ ...f, yetkili_telefon: e.target.value }))} className={`mt-1 h-9 ${inputClass}`} />
                </div>
                <div>
                  <label className={`text-xs font-medium ${labelClass}`}>Yetkili E-posta</label>
                  <Input value={form.yetkili_email} onChange={(e) => setForm((f) => ({ ...f, yetkili_email: e.target.value }))} className={`mt-1 h-9 ${inputClass}`} />
                </div>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className={`text-xs font-medium ${labelClass}`}>Notlar</label>
              <Textarea value={form.notlar} onChange={(e) => setForm((f) => ({ ...f, notlar: e.target.value }))} rows={2} className={`mt-1 ${inputClass}`} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFormOpen(false)} className={isDark ? "border-slate-600" : ""}>İptal</Button>
            <Button type="button" onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-500 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HospitalsPage;
